// components/user/SubscriptionCard.tsx
import React, { useCallback, useState } from "react";
import { UserSubscription, Channel } from "../../types";
import { formatDate, getDaysRemaining, getChannelPlatform } from "../../lib/utils";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import LoadingIndicator from "../../components/ui/LoadingIndicator";
import { SUBSCRIPTION_STATUS, PLATFORMS } from "../../lib/constants";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faFileInvoiceDollar,
} from "@fortawesome/free-solid-svg-icons";
import {
  faCopy,
} from "@fortawesome/free-regular-svg-icons";
import { faTelegram, faDiscord, faSlack } from "@fortawesome/free-brands-svg-icons";

interface SubscriptionCardProps {
  subscription: UserSubscription;
  onRenew: () => void;
  onKYC: () => void;
  onUpgrade: () => void;
  onViewInvoice: () => void;
  onSyncAgreement?: () => void;
  isLoading?: boolean;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  onRenew,
  onKYC,
  onUpgrade,
  onViewInvoice,
  onSyncAgreement,
  isLoading = false,
}) => {
  const [, setIsCopying] = useState(false);

  const fallbackCopy = (text: string) => {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  };

  const handleCopyLink = useCallback(async () => {
    setIsCopying(true);
    try {
      const slug =
        typeof subscription.link_id === "object"
          ? subscription.link_id.url_slug
          : null;
      if (slug) {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(slug);
        } else {
          fallbackCopy(slug);
        }
        toast.success("Channel link copied to clipboard!");
      } else {
        toast.error("Channel link not available.");
      }
    } catch {
      toast.error("Copy failed");
    } finally {
      setIsCopying(false);
    }
  }, [subscription.link_id]);
const handleOpenLink = useCallback(() => {
  try {
    const slug =
      typeof subscription.link_id === "object"
        ? subscription.link_id.url_slug
        : null;

    if (!slug) {
      toast.error("Channel link not available.");
      return;
    }

    // If slug is already a full URL, use directly
    const url = slug.startsWith("http")
      ? slug
      : `https://t.me/${slug}`; // adjust per platform

    window.open(url, "_blank");
  } catch {
    toast.error("Failed to open link");
  }
}, [subscription.link_id]);

  const { status, start_date, end_date, last_transaction_id } = subscription;
  const combo = typeof subscription.combo_id === "object" && subscription.combo_id;
  const plan = typeof subscription.plan_id === "object" && subscription.plan_id;
  const channel =
    typeof subscription.channel_id === "object" && subscription.channel_id;
  const daysRemaining = getDaysRemaining(end_date);

  // Get platform from channel or subscription
  const platform = channel ? getChannelPlatform(channel as Channel) : subscription.platform || PLATFORMS.TELEGRAM;
  const isTelegram = platform === PLATFORMS.TELEGRAM;
  const isDiscord = platform === PLATFORMS.DISCORD;
  const isSlack = platform === PLATFORMS.SLACK;
  const hasLink =
    typeof subscription.link_id === "object" && !!subscription.link_id?.url_slug;

  const canRenew = status === SUBSCRIPTION_STATUS.EXPIRED;
  const canKYC = status === SUBSCRIPTION_STATUS.GETKYC;
  const canSignAgreement = status === SUBSCRIPTION_STATUS.AGSIGN;
  const hasAgreementSigningUrl = !!subscription.digio_signing_url;
  const canLink =
    [SUBSCRIPTION_STATUS.PENDING, SUBSCRIPTION_STATUS.ACTIVE].includes(status) &&
    hasLink;
  const canUpgrade = status === SUBSCRIPTION_STATUS.ACTIVE;
  const canViewInvoice =
    !!last_transaction_id &&
    [SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.EXPIRED].includes(status);

  return (
    <div
      className="
        relative
        bg-dark-secondary
        border border-dark-tertiary
        rounded-lg
        shadow-lg
        p-6
        flex flex-col
        transition
        hover:shadow-xl
        hover:-translate-y-1
        hover:border-golden-accent
        duration-200
      "
    >
      {isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg z-10">
          <LoadingIndicator size="md" textColor="text-text-primary" />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {/* Platform Icon */}
          <div
            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
              isDiscord
                ? "bg-[#5865F2]/20"
                : isSlack
                ? "bg-[#4A154B]/20"
                : "bg-[#0088cc]/20"
            }`}
          >
            <FontAwesomeIcon
              icon={isDiscord ? faDiscord : isSlack ? faSlack : faTelegram}
              className={`text-sm ${isDiscord ? "text-[#5865F2]" : isSlack ? "text-[#4A154B]" : "text-[#0088cc]"}`}
            />
          </div>
          <h3
            className="text-xl font-semibold text-text-primary truncate leading-tight"
            title={channel?.name || ""}
          >
            {channel?.name || "Unknown Channel"}
          </h3>
        </div>
        <Badge status={status} size="sm" />
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 mb-6 text-sm">
        {[
          ["Plan", plan?.name || combo?.name || "N/A"],
          [
            "Price",
            plan ? `₹${plan.discounted_price ?? plan.markup_price}` : combo ? `₹${combo.price}` : "N/A",
          ],
          [
            "Validity",
            plan?.validity_days
              ? `${plan.validity_days} day${plan.validity_days !== 1 ? "s" : ""
              }`
              : combo?.validity_days
                ? `${combo.validity_days} day${combo.validity_days !== 1 ? "s" : ""
                }` : "N/A",
          ],
          ["Started", formatDate(start_date)],
          ["Expires", formatDate(end_date)],
        ].map(([label, val]) => (
          <div key={label} className="flex">
            <span className="w-24 font-medium text-text-secondary">
              {label}:
            </span>
            <span className="text-text-primary">{val ?? "N/A"}</span>
          </div>
        ))}

        {(status === SUBSCRIPTION_STATUS.ACTIVE ||
          status === SUBSCRIPTION_STATUS.EXPIRED) && (
            <div className="sm:col-span-2 mt-3">
              <span
                className={`
                block
                font-medium
                ${status === SUBSCRIPTION_STATUS.ACTIVE
                    ? daysRemaining! > 3
                      ? "text-functional-success"
                      : "text-functional-warning"
                    : "text-status-expired"
                  }
              `}
              >
                {status === SUBSCRIPTION_STATUS.ACTIVE
                  ? daysRemaining! > 0
                    ? `Expires in ${daysRemaining} day${daysRemaining !== 1 ? "s" : ""
                    }`
                    : "Expires today"
                  : `Expired on ${formatDate(end_date)}`}
              </span>
            </div>
          )}
      </div>

      {/* Actions */}
      <div className="mt-auto flex flex-wrap gap-3 justify-end pt-4 border-t border-dark-tertiary">
        {canRenew && (
          <Button onClick={onRenew} size="sm" variant="success">
            Renew
          </Button>
        )}
        {canKYC && (
          <Button onClick={onKYC} size="sm" variant="primary">
            Submit KYC
          </Button>
        )}
        {canLink && (
          <Button onClick={handleOpenLink} size="sm" variant="secondary">
            <FontAwesomeIcon icon={faCopy} className="mr-1" />
            Open Link
          </Button>
        )}
        {canUpgrade && (
          <Button onClick={onUpgrade} size="sm" variant="info">
            <FontAwesomeIcon icon={faArrowUp} className="mr-1" />
            Extend Plan
          </Button>
        )}
        {canViewInvoice && (
          <Button onClick={onViewInvoice} size="sm" variant="secondary">
            <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-1" />
            Invoice
          </Button>
        )}
        {canSignAgreement && (
          <>
            {hasAgreementSigningUrl ? (
              <Button
                onClick={() => window.open(subscription.digio_signing_url, "_blank")}
                size="sm"
                variant="primary"
              >
                Sign Agreement
              </Button>
            ) : (
              <span className="inline-flex items-center rounded-md border border-functional-warning/30 bg-functional-warning/10 px-3 py-1.5 text-xs font-medium text-functional-warning">
                Preparing Agreement...
              </span>
            )}
            <Button onClick={onSyncAgreement} size="sm" variant="secondary">
              I&apos;ve Signed, Refresh Status
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionCard;
